import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import qs from 'qs';

import ButtonLoader from '../../../components/shared/SkeletonLoaders/ButtonLoader/ButtonLoader.jsx';
import Sortby from '../../../components/shared/Sortby/Sortby';
import { JOBS_SORTBY_OPTIONS } from '../../../constants/commonConstants';
import { DEFAULT_PAGE, OPENINGS_DEFAULT_PER_PAGE } from '../../../constants/paginationDefaults.js';
import { useJobFilterContext } from '../../../contexts/JobFilterProvider.js';
import { IsMobile } from '../../../helpers/IsMobile';
import {
  clearOpenings,
  fetchNextOpenings,
  getOpeningDetail,
  getOpenings,
  getSearchOpenings,
  setSortby,
} from '../../../redux/actions/browseActions';
import { useGetUserProfileQuery } from '../../../redux/apis/userApi';
import { selectUserId, selectUserUuid } from '../../../selectors/authSelectors';
import {
  selectOpeningList,
  selectOpeningListLoading,
  selectPaginationInfo,
  selectSortBy,
} from '../../../selectors/browseSelectors';
import { selectCurrentOrganization } from '../../../selectors/organizationSelectors';
import OpeningDetail from '../OpeningDetail/OpeningDetail';
import OpeningSearchAndFilter from '../OpeningSearchAndFilter/OpeningSearchAndFilter';
import OpeningsListWrapper from '../OpeningsListWrapper/OpeningsListWrapper';

import styles from './OpeningsContainer.module.scss';

const BROWSE_KEYS = {
  all: ['job', 'department'],
};

const OpeningsContainer = ({ tabkey, isPublic }) => {
  const { t } = useTranslation('common');
  const [searchParams] = useSearchParams();
  const jobId = searchParams.get('id');
  const sortby = useSelector(selectSortBy);
  const isLoading = useSelector(selectOpeningListLoading);
  const userId = useSelector(selectUserId);
  const openings = useSelector(selectOpeningList);
  const openingsList = [...new Map(openings.map((item) => [item.id, item])).values()];
  const currentOrganization = useSelector(selectCurrentOrganization);
  const { initialFilters, updateSearchParams } = useJobFilterContext();
  const isMobile = IsMobile();
  const [jobOrganizationId, setJobOrganizationId] = useState();
  const [paginationNo, setPaginationNo] = useState();
  const userUuid = useSelector(selectUserUuid);

  const hasMounted = useRef(false);
  const [fetchJobsByName, setFetchJobsByName] = useState({
    keys: BROWSE_KEYS[tabkey],
    page: DEFAULT_PAGE,
    per_page: OPENINGS_DEFAULT_PER_PAGE,
    query: '',
    user_id: userId,
    org_id: currentOrganization?.id,
  });
  const dispatch = useDispatch();
  const locationFromParams = JSON.parse(searchParams.get('location'));
  const filterInitialValues = {
    keys: BROWSE_KEYS[tabkey],
    page: DEFAULT_PAGE,
    per_page: OPENINGS_DEFAULT_PER_PAGE,
    query: searchParams.get('query') || '',
    user_id: userId,
    location: locationFromParams,
    sort_by: locationFromParams ? JOBS_SORTBY_OPTIONS[2].value : JOBS_SORTBY_OPTIONS[0].value,
    applied: searchParams.get('applied'),
    referred: searchParams.get('referred'),
    filters: initialFilters,
    org_id: currentOrganization?.id,
  };
  const [filters, setfilters] = useState(filterInitialValues);
  const paginationInfo = useSelector(selectPaginationInfo);
  const referralProviderId = searchParams.get('rp');
  const { data: referralProviderData } = useGetUserProfileQuery(referralProviderId, { skip: !referralProviderId });

  useEffect(() => {
    if (referralProviderId && referralProviderData?.payload) {
      localStorage.setItem('referralProviderData', JSON.stringify(referralProviderData?.payload));
    }
  }, [referralProviderId, referralProviderData]);

  useEffect(() => {
    if (currentOrganization?.id || (currentOrganization && !currentOrganization?.id)) {
      setfilters((prevFilters) => ({
        ...prevFilters,
        org_id: currentOrganization?.id,
      }));
      setFetchJobsByName((prevFilters) => ({
        ...prevFilters,
        org_id: currentOrganization?.id,
      }));
      setJobOrganizationId(currentOrganization?.id);
    }
  }, [currentOrganization]);

  useEffect(() => {
    if (paginationNo > 1) {
      const encodedPageFilters = qs.stringify(
        { ...filters, page: paginationNo },
        { arrayFormat: 'brackets' }
      );
      dispatch(fetchNextOpenings(encodedPageFilters));
    }
  }, [paginationNo]);

  useEffect(() => {
    const currentLocation = filters.location;
    const hasLocation = !!currentLocation;
    const currentSortBy = filters.sort_by;

    const expectedSortBy = hasLocation ? JOBS_SORTBY_OPTIONS[2].value : JOBS_SORTBY_OPTIONS[0].value;

    if (currentSortBy !== expectedSortBy) {
      const newSortBy = hasLocation ? JOBS_SORTBY_OPTIONS[2] : JOBS_SORTBY_OPTIONS[0];
      setfilters((prevFilters) => ({
        ...prevFilters,
        sort_by: expectedSortBy,
        page: DEFAULT_PAGE,
      }));
      dispatch(setSortby(newSortBy));
    }
  }, [filters.location, dispatch]);

  useEffect(() => {
    if (currentOrganization) {
      const isJobExists = openingsList.find((job) => job.uuid === jobId);
      const encodedFilters = qs.stringify(filters, { arrayFormat: 'brackets' });
      dispatch(clearOpenings());
      dispatch(
        getOpenings(encodedFilters, (id) => {
          if (!jobId || isJobExists) {
            updateSearchParams('id', id);
            dispatch(getOpeningDetail(id, userUuid));
          }
          setPaginationNo(DEFAULT_PAGE);
        })
      );
    }
  }, [filters, jobOrganizationId]);

  useEffect(() => {
    if (
      (hasMounted.current && jobOrganizationId) ||
      (hasMounted.current && currentOrganization && !jobOrganizationId)
    ) {
      const encodedFilters = qs.stringify(fetchJobsByName, { arrayFormat: 'brackets' });
      dispatch(getSearchOpenings(encodedFilters, true));
    } else {
      hasMounted.current = true;
    }
  }, [fetchJobsByName.query]);

  const handleSortbyChange = useCallback(
    (sortBy) => {
      setfilters({ ...filters, sort_by: sortBy.value, page: DEFAULT_PAGE });
      dispatch(setSortby(sortBy));
    },
    [filters]
  );

  return (
    <div className={isPublic ? styles.openingsContainerWrapperNonLogged : styles.openingsContainerWrapper}>
      <OpeningSearchAndFilter setfilters={setfilters} filters={filters} setFetchJobsByName={setFetchJobsByName} />
      <div className={styles.flexContainer}>
        <div className={styles.openingListFlexItem}>
          {isLoading ? (
            <div className={styles.sortLoader}>
              <ButtonLoader count={3} width={100} height={8} />
            </div>
          ) : (
            <div className={styles.sortContainer}>
              <div className={styles.jobsCount}>{t('browse.jobsFound', { count: paginationInfo?.count || 0 })}</div>
              <Sortby
                jobSorting
                sortOptions={filters.location ? JOBS_SORTBY_OPTIONS : JOBS_SORTBY_OPTIONS.slice(0, 2)}
                value={sortby}
                defaultValue={filters.sort_by}
                onChange={handleSortbyChange}
              />
            </div>
          )}
          <OpeningsListWrapper paginationNo={paginationNo} setPaginationNo={setPaginationNo} isPublic={isPublic} />
        </div>
        {!isMobile && (
          <div className={styles.openingDetailsFlexItem}>
            <OpeningDetail isPublic={isPublic} fromBrowse />
          </div>
        )}
      </div>
    </div>
  );
};

export default OpeningsContainer;
